import { AxiosResponse } from 'axios';

const CACHE_TIME_30S = 30000;
const CACHE_TIME_1MIN = 60000;
const CACHE_TIME_2MIN = 120000;
const CACHE_TIME_5MIN = 300000;
const CACHE_TIME_10MIN = 600000;

interface CacheItem {
  promise: Promise<any>; // 请求项
  expireTime: number; // 请求结果保持时间
  timeoutId?: ReturnType<typeof setTimeout>; // 自动清理定时器
  forceRefresh?: boolean; // 强制请求
}

if (!window.top.__mpRequestCache) {
  window.top.__mpRequestCache = new Map<symbol | string, CacheItem>();
}

if (!window.top.__mpPrefetchCacheApis) {
  window.top.__mpPrefetchCacheApis = {
    // 门店商品列表
    '/api/v1/shop/product/render': {
      cacheTime: CACHE_TIME_2MIN,
      forceRefresh: false, // 可动态设置的状态
      defaultRefresh: false, // 默认状态
    },
    // 门店商品列表
    '/api/v1/shop/shopInfo': { cacheTime: CACHE_TIME_2MIN },
    '/api/v1/shop/productList': { cacheTime: CACHE_TIME_2MIN },
    // 个人页
    '/api/v1/userinfo/getMyPageModule': { cacheTime: CACHE_TIME_5MIN },
    // 订单列表
    '/api/v1/order/list': {
      cacheTime: CACHE_TIME_5MIN,
      defaultRefresh: true, // 常态都刷新， 仅在页面初始化时不刷新
    },
    // 首页
    '/api/v2/homePage/homePageInfo': {
      cacheTime: CACHE_TIME_10MIN,
      defaultRefresh: true, // 常态都刷新， 仅在页面初始化时不刷新
      // defaultRefresh: false,
    },
    // 首页v4
    '/api/v4/homePage/homePageInfo': {
      cacheTime: CACHE_TIME_10MIN,
      defaultRefresh: true, // 常态都刷新， 仅在页面初始化时不刷新
      // defaultRefresh: false,
    },
    '/api/v3/homePage/homeShopList': {
      cacheTime: CACHE_TIME_5MIN,
      defaultRefresh: true, // 常态都刷新， 仅在页面初始化时不刷新
      // defaultRefresh: false,
    },
  };
  // // 要缓存的请求
  // // url.replace('/api/v1/', '/api/v1/h5/m/');
  // // url.replace(/^\/api\//, '/api/h5/');
  // Object.keys({ ...window.top.__mpPrefetchCacheApis }).forEach((url: string) => {
  //   const newUrl = url.replace(/^\/api\//, '/api/h5/');
  //   const newMUrl = url.replace('/api/v1/', '/api/v1/h5/m/');
  //   if (newUrl !== url) {
  //     window.top.__mpPrefetchCacheApis[newUrl] = window.top.__mpPrefetchCacheApis[url];
  //   }
  //   if (newMUrl !== url) {
  //     window.top.__mpPrefetchCacheApis[newMUrl] = window.top.__mpPrefetchCacheApis[url];
  //   }
  // });
}

// 获取url对应的缓存时间。 有配置则会统一使用createPrefetchApi模式
export const getUrlCacheTime = (url) => {
  return window.top.__mpPrefetchCacheApis[url]?.cacheTime;
};

// 获取url对应的缓存时间。 有配置则会统一使用createPrefetchApi模式
export const getUrlCache = (url) => {
  return window.top.__mpPrefetchCacheApis[url];
};

// 设置url强制刷新
export const setUrlForceRefresh = (url, forceRefresh = false) => {
  if (window.top.__mpPrefetchCacheApis[url]) {
    window.top.__mpPrefetchCacheApis[url] = {
      ...window.top.__mpPrefetchCacheApis[url],
      forceRefresh,
    };
  }
};

const setCache = (key, item) => {
  window.top.__mpRequestCache.set(key, item);
};

const getCache = (key) => {
  return window.top.__mpRequestCache.get(key);
};

const deleteCache = (key) => {
  window.top.__mpRequestCache.delete(key);
};

// 设置强制请求
export const setForceRefresh = (key, forceRefresh = true) => {
  const cacheRequest = getCache(key); // CacheItem
  if (cacheRequest && !cacheRequest.forceRefresh) {
    setCache(key, {
      ...cacheRequest,
      forceRefresh,
    });
  }
};

// 重置全部接口预取缓存：清理各项自动清理定时器并清空缓存 Map。
// 切换 Region 后旧 Region 的接口数据全部失效，跳转/刷新前调用，避免命中旧数据。
export const resetPrefetchCache = () => {
  const cache = window.top.__mpRequestCache;
  if (!cache) {
    return;
  }
  cache.forEach((item: CacheItem) => {
    if (item?.timeoutId) {
      clearTimeout(item.timeoutId);
    }
  });
  cache.clear();
};

// 设置缓存项的自动清理
const setCacheWithAutoCleanup = (
  key: symbol | string,
  item: CacheItem,
  cacheTime: number
) => {
  // 清除旧的定时器
  if (item.timeoutId) {
    clearTimeout(item.timeoutId);
  }

  // 设置新的自动清理定时器
  item.timeoutId = setTimeout(() => {
    deleteCache(key);
  }, cacheTime + 5000);
  setCache(key, item);
};

// 只要接口依赖信息就绪，就可以发起请求，实际使用数据的位置会继续上一次已发起的请求
export const createPrefetchApi = (
  cacheKey: string | symbol,
  requestFn: (any?) => Promise<AxiosResponse>,
  cacheTime = 30 * 1000
) =>
  async (params, forceRefresh = false) => {
    const now = Date.now();
    const cacheRequest = getCache(cacheKey); // CacheItem
    console.log(params.url, cacheKey, 'createPrefetchApi in', forceRefresh, cacheRequest?.forceRefresh);
    if (
      forceRefresh
      || !window.top.__mpRequestCache?.has?.(cacheKey)
      || (cacheRequest?.expireTime || 0) <= now
      || cacheRequest?.forceRefresh
    ) {
      const promise = requestFn(params)
        .then((result: any) => {
          if (cacheTime > 0) {
            setCacheWithAutoCleanup(cacheKey, { promise, expireTime: now + cacheTime, forceRefresh: false }, cacheTime);
          } else {
            deleteCache(cacheKey);
          }
          return result;
        })
        .catch((error: any) => {
          const cacheItem = getCache(cacheKey);
          if (cacheItem?.timeoutId) {
            clearTimeout(cacheItem.timeoutId);
          }
          deleteCache(cacheKey);
          throw error;
        });

      if (cacheTime > 0) {
        setCacheWithAutoCleanup(cacheKey, { promise, expireTime: now + cacheTime, forceRefresh: false }, cacheTime);
      }
      return promise;
    }
    return cacheRequest?.promise;
  };
